import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

async function assertCallerIsAdmin(callerUserId: string) {
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", callerUserId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

export interface AuditLead {
  id: string;
  full_name: string;
  email: string;
  locale: "en" | "es";
  created_at: string;
  store_url: string | null;
  overall_score: number | null;
  checks_summary: { pass: number; warning: number; fail: number };
  message: string;
}

const AUDIT_MARKER = "Free Store Audit Tool";

function parseAuditMessage(message: string) {
  const storeMatch = message.match(/Store URL:\s*(\S+)/i);
  const scoreMatch = message.match(/Overall score:\s*(\d+)\s*\/\s*100/i);
  const passes = (message.match(/:\s*PASS/g) ?? []).length;
  const warnings = (message.match(/:\s*WARNING/g) ?? []).length;
  const fails = (message.match(/:\s*FAIL/g) ?? []).length;
  return {
    store_url: storeMatch?.[1] ?? null,
    overall_score: scoreMatch ? parseInt(scoreMatch[1], 10) : null,
    checks_summary: { pass: passes, warning: warnings, fail: fails },
  };
}

export const listAuditLeads = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ leads: AuditLead[] }> => {
    await assertCallerIsAdmin(context.userId);

    const { data, error } = await supabaseAdmin
      .from("contact_submissions")
      .select("id, full_name, email, message, locale, created_at")
      .ilike("message", `%${AUDIT_MARKER}%`)
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);

    const leads: AuditLead[] = (data ?? []).map((row) => {
      const parsed = parseAuditMessage(row.message ?? "");
      return {
        id: row.id,
        full_name: row.full_name,
        email: row.email,
        locale: (row.locale === "es" ? "es" : "en") as "en" | "es",
        created_at: row.created_at,
        message: row.message ?? "",
        ...parsed,
      };
    });
    return { leads };
  });
